import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Floating "Install Maestro" prompt. Listens to the standard `beforeinstallprompt`
 * event exposed by Chrome, Edge, Samsung Internet and Android WebView. Shown once,
 * dismissal persisted in localStorage for 14 days.
 */
const DISMISS_KEY = 'maestro:pwa-install-dismissed';
const DISMISS_DAYS = 14;

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return (Date.now() - ts) < DISMISS_DAYS * 24 * 3600 * 1000;
  } catch { return false; }
}

export default function InstallPWAPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed / running standalone → don't prompt
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (standalone) return;
    if (wasDismissedRecently()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Small delay so it doesn't distract from the initial page load
      setTimeout(() => setVisible(true), 3500);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // If the app gets installed while the tab is open, dismiss the banner
    const installedHandler = () => setVisible(false);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch (err) { console.debug('PWA install failed', err); }
    finally {
      setDeferred(null);
      setVisible(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible || !deferred) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed z-50 bottom-4 right-4 left-4 md:left-auto md:w-96 bg-stone-900 border border-orange-800/60 rounded-2xl shadow-2xl p-4 flex items-start gap-3 rise"
      role="dialog"
      aria-labelledby="pwa-install-title"
    >
      <div className="w-11 h-11 shrink-0 rounded-xl bg-orange-700/20 border border-orange-800/60 flex items-center justify-center">
        <Download size={20} className="text-orange-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div id="pwa-install-title" className="font-semibold text-stone-100">
          Installer Maestro
        </div>
        <p className="text-stone-400 text-xs mt-0.5">
          Ajoute Maestro à ton écran d'accueil pour un lancement instantané et un accès hors-ligne à ta bibliothèque.
        </p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-700 hover:bg-orange-600 text-white text-xs font-semibold transition-colors"
          >
            <Download size={12} /> Installer
          </button>
          <button
            onClick={dismiss}
            data-testid="pwa-install-later"
            className="px-3 py-1.5 rounded-full bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs"
          >
            Plus tard
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label="Fermer"
        className="text-stone-500 hover:text-stone-300 shrink-0"
      >
        <X size={16} />
      </button>
    </div>
  );
}
